import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { AudioVariantSummary, CatalogSong, LyricLineTiming } from "@vocab1600/shared";
import { api, ApiError } from "./api.js";
import { toPlayItem, useAudioPlayer } from "./player-context.js";

function fmt(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0:00.0";
  const total = ms / 1000;
  const m = Math.floor(total / 60);
  const s = (total % 60).toFixed(1).padStart(4, "0");
  return `${m}:${s}`;
}

function parseTime(text: string): number | null {
  const match = text.trim().match(/^(?:(\d+):)?(\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const minutes = Number(match[1] ?? 0);
  const seconds = Number(match[2]);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
  return Math.round((minutes * 60 + seconds) * 1000);
}

interface DraftLine extends LyricLineTiming {
  marked: boolean;
}

/**
 * 歌词校准工作台。
 *
 * 工作方式：边听边打点。播放音频，在每一行开始唱到时按空格（或点「标记本行」），
 * 记录当前播放位置作为该行起点；下一行的起点即上一行的终点，最后一行到音频结束。
 *
 * 为什么必须逐版本校准：同一首歌的 A/B/C/D 是不同旋律，节奏、前奏长度、
 * 重复次数都不同。共用一套时间戳会让跟唱整体错拍，比没有同步更糟。
 * 因此时间轴挂在 audio_variant 上，而不是挂在歌曲上。
 */
export function LyricStudio() {
  const { songNumber = "" } = useParams();
  const navigate = useNavigate();
  const player = useAudioPlayer();
  const [variants, setVariants] = useState<AudioVariantSummary[]>([]);
  const [variantKey, setVariantKey] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [cursor, setCursor] = useState(0);
  const [status, setStatus] = useState<string>("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [trackStatus, setTrackStatus] = useState<string>("未校准");
  const listRef = useRef<HTMLDivElement | null>(null);

  const song: CatalogSong | undefined = useMemo(
    () => (window.APP_SONGS ?? []).find((item) => item.num === songNumber),
    [songNumber]
  );

  // 歌词行来自内容目录，是所有版本共用的文本；时间戳才是逐版本的
  const catalogLines = useMemo(() => {
    if (!song) return [];
    const out: LyricLineTiming[] = [];
    let index = 0;
    for (const section of song.sections) {
      for (const line of section.lines) {
        out.push({ lineIndex: index, lineKey: line.key, section: section.name, text: line.raw, startMs: 0, endMs: 0 });
        index += 1;
      }
    }
    return out;
  }, [song]);

  useEffect(() => {
    if (!songNumber) return;
    api.songVariants(songNumber)
      .then((data) => {
        setVariants(data.variants);
        setVariantKey((current) => current || data.variants[0]?.variantKey || "");
      })
      .catch(() => setError("无法读取旋律版本"));
  }, [songNumber]);

  // 切换版本时载入该版本已有的时间轴；没有则用空白草稿
  useEffect(() => {
    if (!variantKey || catalogLines.length === 0) return;
    setCursor(0);
    api.lyricsDraft(variantKey)
      .then((track) => {
        setTrackStatus(track.status === "published" ? "已发布" : "草稿");
        const byKey = new Map(track.lines.map((line) => [line.lineKey, line]));
        setLines(catalogLines.map((line) => {
          const saved = byKey.get(line.lineKey);
          return saved
            ? { ...line, startMs: saved.startMs, endMs: saved.endMs, marked: true }
            : { ...line, marked: false };
        }));
      })
      .catch((cause) => {
        if (cause instanceof ApiError && cause.status === 404) {
          setTrackStatus("未校准");
          setLines(catalogLines.map((line) => ({ ...line, marked: false })));
        } else {
          setError("读取已有时间轴失败");
        }
      });
  }, [variantKey, catalogLines]);

  const activeVariant = variants.find((v) => v.variantKey === variantKey);

  const mark = useCallback(() => {
    const position = player.state.positionMs;
    setLines((prev) => {
      const next = [...prev];
      const line = next[cursor];
      if (!line) return prev;
      next[cursor] = { ...line, startMs: position, marked: true };
      // 上一行的结束点自动衔接到本行起点，避免出现空档
      const previous = next[cursor - 1];
      if (previous) next[cursor - 1] = { ...previous, endMs: position };
      // 最后一行暂时以音频总时长收尾
      const duration = player.state.durationMs || position + 3000;
      next[cursor] = { ...next[cursor]!, endMs: Math.max(duration, position + 500) };
      return next;
    });
    setCursor((c) => Math.min(c + 1, catalogLines.length - 1));
  }, [cursor, catalogLines.length, player.state.durationMs, player.state.positionMs]);

  // 空格打点、方向键移动光标；输入框内不拦截
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") return;
      if (event.code === "Space") { event.preventDefault(); mark(); }
      else if (event.code === "ArrowDown") { event.preventDefault(); setCursor((c) => Math.min(c + 1, lines.length - 1)); }
      else if (event.code === "ArrowUp") { event.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
      else if (event.code === "KeyP") {
        event.preventDefault();
        if (activeVariant) song && activeVariant && player.toggle(toPlayItem(song, activeVariant));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mark, lines.length, activeVariant, player, song]);

  // 光标行滚动到可视区
  useEffect(() => {
    const container = listRef.current;
    const row = container?.querySelector<HTMLElement>(`[data-index="${cursor}"]`);
    if (container && row) {
      const top = row.offsetTop - container.clientHeight / 2;
      container.scrollTo({ top, behavior: "smooth" });
    }
  }, [cursor]);

  async function save(publish: boolean) {
    setSaving(true); setError(""); setStatus("");
    const marked = lines.filter((line) => line.marked);
    if (publish && marked.length !== lines.length) {
      setError(`还有 ${lines.length - marked.length} 行未打点，发布前需要全部标记`);
      setSaving(false);
      return;
    }
    try {
      const payload = marked.map(({ marked: _drop, ...line }) => line);
      const saved = await api.saveLyrics(variantKey, { publish, lines: payload });
      setTrackStatus(saved.status === "published" ? "已发布" : "草稿");
      setStatus(publish ? "已发布，学生端播放时会显示逐行高亮" : `草稿已保存（${marked.length} 行）`);
    } catch (cause) {
      setError(cause instanceof ApiError ? (cause.detail ?? cause.code) : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  if (!song) {
    return <div className="page">
      <p className="muted">找不到编号为 {songNumber} 的歌曲。</p>
      <button className="ghost-button" onClick={() => navigate("/songs")}>返回歌单</button>
    </div>;
  }

  const markedCount = lines.filter((l) => l.marked).length;

  return <div className="page">
    <header className="page-header">
      <span className="eyebrow dark">LYRIC STUDIO</span>
      <h1>歌词校准 · {song.num} {song.title}</h1>
      <p>
        播放音频，在每一行唱到时按 <kbd>空格</kbd> 打点。<kbd>P</kbd> 播放/暂停，
        <kbd>↑</kbd><kbd>↓</kbd> 移动光标。时间轴按旋律版本分别保存——不同旋律节奏不同，不能共用。
      </p>
    </header>

    <div className="studio-bar panel">
      <label className="inline-field">
        版本
        <select value={variantKey} onChange={(e) => setVariantKey(e.target.value)}>
          {variants.map((variant) => (
            <option key={variant.variantKey} value={variant.variantKey}>
              {variant.variantCode}　{fmt(variant.durationMs)}
            </option>
          ))}
        </select>
      </label>
      <button className="ghost-button" onClick={() => activeVariant && song && activeVariant && player.toggle(toPlayItem(song, activeVariant))}>
        {player.state.playing ? "暂停" : "播放"}
      </button>
      <span className="mono">{fmt(player.state.positionMs)} / {fmt(player.state.durationMs)}</span>
      <button className="primary-button compact" onClick={mark}>标记本行（空格）</button>
      <span className="pill">{trackStatus}</span>
      <span className="muted small">已打点 {markedCount} / {lines.length}</span>
      <div className="studio-actions">
        <button className="ghost-button" onClick={() => void save(false)} disabled={saving}>保存草稿</button>
        <button className="primary-button compact" onClick={() => void save(true)} disabled={saving || markedCount === 0}>
          发布
        </button>
      </div>
    </div>

    {error && <div className="form-error block">{error}</div>}
    {status && <div className="form-notice">{status}</div>}
    {player.state.error && <div className="form-error block">{player.state.error}</div>}

    <section className="panel">
      <div className="studio-lines" ref={listRef}>
        {lines.map((line, index) => (
          <div
            key={line.lineKey}
            data-index={index}
            className={`studio-line${index === cursor ? " cursor" : ""}${line.marked ? " marked" : ""}`}
            onClick={() => setCursor(index)}
          >
            <span className="studio-index">{index + 1}</span>
            <span className="studio-section">{line.section}</span>
            <span className="studio-text">{line.text}</span>
            <input
              className="studio-time mono"
              value={line.marked ? fmt(line.startMs) : ""}
              placeholder="未打点"
              onChange={(event) => {
                const ms = parseTime(event.target.value);
                if (ms === null) return;
                setLines((prev) => {
                  const next = [...prev];
                  const target = next[index];
                  if (!target) return prev;
                  next[index] = { ...target, startMs: ms, marked: true };
                  const previous = next[index - 1];
                  if (previous?.marked) next[index - 1] = { ...previous, endMs: ms };
                  return next;
                });
              }}
            />
            <button className="link-button" onClick={(event) => {
              event.stopPropagation();
              if (line.marked) player.seek(line.startMs);
            }} disabled={!line.marked}>试听</button>
          </div>
        ))}
      </div>
    </section>

    <button className="ghost-button" onClick={() => navigate(`/songs/${song.num}`)}>← 返回歌曲页</button>
  </div>;
}
